import React, { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import Table from '@components/common/react-table/Table';
import { SortOrder } from '@constants/common.constants';

import {
    HISTORICAL_NOTICES_INITIAL_PAGE_NUMBER,
    type IHistoricalNotice,
    type IHistoricalNoticesFetchRequest,
    type HistoricalNoticesSortField,
} from '../constants';
import {
    columnIdToSortField,
    sortFieldToColumnId,
} from './noticesSort.utils';
import { historicalNoticesTableColumnStyles } from './historicalNoticesTable.styles';
import { historicalNoticesTableColumns } from './historicalNoticesTableColumns';

const TableScroll = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;

    table {
        width: 100%;
        min-width: 75rem;
    }

    th,
    td {
        overflow: hidden;
        vertical-align: middle;
    }

    ${historicalNoticesTableColumnStyles}
`;

export interface IHistoricalNoticesViewProps {
    notices: IHistoricalNotice[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    sortColumn: HistoricalNoticesSortField;
    sortOrder: SortOrder;
    onFetch: (options: Partial<IHistoricalNoticesFetchRequest>) => void;
    hasLoadError?: boolean;
    pageTitleMessageId?: string;
    emptyMessageId?: string;
    missingNoticesMessageId?: string;
    loadErrorMessageId?: string;
    tableId?: string;
    screenTestId?: string;
}

const HistoricalNoticesView: React.FC<IHistoricalNoticesViewProps> = ({
    notices,
    totalCount,
    pageNumber,
    pageSize,
    sortColumn,
    sortOrder,
    onFetch,
    hasLoadError = false,
    pageTitleMessageId = 'historicalNotices.title',
    emptyMessageId = 'historicalNotices.msg.empty',
    missingNoticesMessageId = 'historicalNotices.msg.missingNotices',
    loadErrorMessageId = 'historicalNotices.errors.loadFailed',
    tableId = 'historical-notices',
    screenTestId = 'historical-notices-screen',
}) => {
    const skipSortOnMount = useRef(true);

    useEffect(() => {
        skipSortOnMount.current = false;
    }, []);

    const handleSort = useCallback(
        (sortBy: { id: string; desc: boolean }[]) => {
            if (skipSortOnMount.current) {
                return;
            }

            const nextSort = sortBy[0];
            if (!nextSort) {
                return;
            }

            const field = columnIdToSortField(nextSort.id);
            if (!field) {
                return;
            }

            onFetch({
                pageNumber: HISTORICAL_NOTICES_INITIAL_PAGE_NUMBER,
                pageSize,
                sortColumn: field,
                sortOrder: nextSort.desc ? SortOrder.DESC : SortOrder.ASC,
            });
        },
        [onFetch, pageSize],
    );

    const handlePagination = useCallback(
        (nextPage: { page: number; pageSize: number }) => {
            onFetch({
                pageNumber: nextPage.page - 1,
                pageSize: nextPage.pageSize,
                sortColumn,
                sortOrder,
            });
        },
        [onFetch, sortColumn, sortOrder],
    );

    return (
        <div className="gi-panel" data-testid={screenTestId}>
            <h4>
                <FormattedMessage id={pageTitleMessageId} />
            </h4>
            <div role="status" aria-live="polite" aria-atomic="true">
                <ConditionalRendering isTrue={!hasLoadError && notices.length === 0}>
                    <AlertMessage
                        content={<FormattedMessage id={missingNoticesMessageId} />}
                        type="primary"
                        showIcon={false}
                        ariaLive={null}
                    />
                    <AlertMessage
                        content={<FormattedMessage id={emptyMessageId} />}
                        type="primary"
                        showIcon={false}
                        className="gi_warning margin-t-10-ve"
                        ariaLive={null}
                    />
                </ConditionalRendering>
            </div>
            <div role="alert" aria-live="assertive" aria-atomic="true">
                <ConditionalRendering isTrue={hasLoadError}>
                    <AlertMessage
                        content={<FormattedMessage id={loadErrorMessageId} />}
                        type="error"
                        showIcon={false}
                        ariaLive={null}
                    />
                </ConditionalRendering>
            </div>
            <ConditionalRendering isTrue={!hasLoadError && notices.length > 0}>
                <TableScroll>
                    <Table
                        id={tableId}
                        className="mt-4"
                        variant="simple"
                        columns={historicalNoticesTableColumns}
                        data={notices}
                        rowsPerPage={pageSize}
                        totalCount={totalCount}
                        onPagination={handlePagination}
                        selectedPage={pageNumber + 1}
                        onSortChange={handleSort}
                        sortById={sortFieldToColumnId(sortColumn)}
                        sortDesc={sortOrder === SortOrder.DESC}
                        isShowRowsPerPage
                        allowSort
                        manualSortBy
                        hasPagination
                        hasBorders
                        isMobileCardViewEnabled
                    />
                </TableScroll>
            </ConditionalRendering>
        </div>
    );
};

export default HistoricalNoticesView;
